import { Entities_TeamJtfSummary } from '@suntex.fim/api-client';
import {
  $ArithmeticOperation,
  $JtfMode,
  ArithmeticOperation,
  JtfMode,
} from '@suntex.fim/enums';

/**
 * Builds the Just The Facts mode selector options from the modes the report
 * actually returned, so the dropdown only offers modes we have data for.
 */
export function getJtfModeOptions(
  jtfModes: JtfMode[] | undefined,
): { key: JtfMode; label: string }[] {
  const descriptors = $JtfMode.describeAll();

  // No modes reported: offer every mode.
  if (!jtfModes || !jtfModes.length) {
    return descriptors.map((descriptor) => ({
      key: descriptor.value,
      label: descriptor.label,
    }));
  }

  // jtfModes may arrive as slugs at runtime though typed as JtfMode values, so
  // match each entry to its descriptor by value or slug. An unrecognized entry
  // is logged and skipped rather than thrown, so bad data can't blank the report.
  return jtfModes.flatMap((mode) => {
    const descriptor = descriptors.find(
      (candidate) =>
        candidate.value === mode || candidate.slug === String(mode),
    );

    if (!descriptor) {
      // eslint-disable-next-line no-console
      console.error(`Unrecognized JTF mode in report data: ${String(mode)}`);
      return [];
    }

    return [{ key: descriptor.value, label: descriptor.label }];
  });
}

/**
 * Picks the Just The Facts mode to display: keeps the current selection when the
 * report still offers it, otherwise falls back to the first offered mode. This
 * keeps the default (Whole Numbers) from landing on a mode the dropdown no
 * longer offers (e.g. grade 7-8 exclude Whole Numbers), which would otherwise
 * leave the selector on "Select level" with an empty chart.
 */
export function resolveSelectedJtfMode(
  jtfModes: JtfMode[] | undefined,
  currentMode: JtfMode,
): JtfMode {
  const offered = getJtfModeOptions(jtfModes);
  if (offered.some((option) => option.key === currentMode)) {
    return currentMode;
  }

  return offered[0]?.key ?? currentMode;
}

const PRE = 'PRE';
const POST = 'POST';

export type JtfStage = typeof PRE | typeof POST;

export const JtfStage = {
  PRE,
  POST,
} as const;

// justTheFactsMode/arithmeticOp serialize as slugs at runtime though typed as
// numeric enums, so match on either the value or the slug.
function jtfModeMatches(value: JtfMode, mode: JtfMode): boolean {
  return value === mode || String(value) === $JtfMode.describe(mode).slug;
}

function arithmeticOpMatches(
  value: ArithmeticOperation,
  operation: ArithmeticOperation,
): boolean {
  return (
    value === operation ||
    String(value) === $ArithmeticOperation.describe(operation).slug
  );
}

export function getCalculatedPercentage(
  jtfSummary: (Entities_TeamJtfSummary | undefined)[] | undefined,
  mode: JtfMode,
  operation: ArithmeticOperation,
  stage: JtfStage,
): number {
  let value = 0;
  if (!jtfSummary) return value;

  const matchingItem = jtfSummary.find(
    (item) =>
      !!item &&
      jtfModeMatches(item.justTheFactsMode, mode) &&
      arithmeticOpMatches(item.arithmeticOp, operation),
  );

  if (matchingItem) {
    const tier1 =
      stage === PRE
        ? matchingItem.jtfByEntityAllOps.tieR1NUMBERPRE
        : matchingItem.jtfByEntityAllOps.tieR1NUMBERPOST;
    const tier2 =
      stage === PRE
        ? matchingItem.jtfByEntityAllOps.tieR2NUMBERPRE
        : matchingItem.jtfByEntityAllOps.tieR2NUMBERPOST;

    const rawTotalStudents =
      matchingItem.jtfByEntityAllOps.totalstudentsplaying;
    const totalStudents = rawTotalStudents > 0 ? rawTotalStudents : 1; // Avoid division by 0

    // Apply the formula and return the percentage
    const PERCENTAGE_MULTIPLIER = 100;
    value = Math.round(
      ((tier1 + tier2) / totalStudents) * PERCENTAGE_MULTIPLIER,
    ); // Round to nearest integer
  }

  return value;
}
